import type { input } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PaymentSettingsFormData, paymentSettingsSchema } from '../schema';
import { useGetPaymentSettings } from '../queries';
import useMutateData from './useMutateData';
import API_ROUTES from '../constants/apiRoutes';
import { DEPOSIT_APPROVAL_MODE_OPTIONS, QUERY_KEYS, WITHDRAWAL_APPROVAL_MODE_OPTIONS } from '../constants';
import { APPROVAL_MODE, PAYMENT_TYPE } from '../constants/enums';
import { PaymentType } from '../@types/api';
import { SelectOption } from '../@types';
import { useMarketplace } from './useMarketplace';

const PAYMENT_CONFIGS: Record<
  PaymentType,
  {
    queryKey: string;
    approvalModeOptions: SelectOption[];
  }
> = {
  [PAYMENT_TYPE.DEPOSIT]: {
    queryKey: QUERY_KEYS.depositConfiguration,
    approvalModeOptions: DEPOSIT_APPROVAL_MODE_OPTIONS,
  },
  [PAYMENT_TYPE.WITHDRAWAL]: {
    queryKey: QUERY_KEYS.withdrawalConfiguration,
    approvalModeOptions: WITHDRAWAL_APPROVAL_MODE_OPTIONS,
  },
};

export const usePaymentSettings = (paymentType: PaymentType) => {
  const config = PAYMENT_CONFIGS[paymentType];
  const { config: marketplace } = useMarketplace();

  const { data: paymentConfig } = useGetPaymentSettings(paymentType);

  const { createData } = useMutateData(API_ROUTES.PAYMENT_LIMIT_CONFIGURATIONS, [config.queryKey], 'wallet');

  const form = useForm<input<typeof paymentSettingsSchema>, unknown, PaymentSettingsFormData>({
    values: paymentConfig,
    resolver: zodResolver(paymentSettingsSchema),
  });

  const approvalMode = form.watch('approvalMode');

  const onFormSubmit = (data: PaymentSettingsFormData) => {
    const submitData = approvalMode === APPROVAL_MODE.NONE ? { approvalMode: data.approvalMode } : data;

    createData({ ...submitData, type: paymentType });
  };

  const hideInputs = approvalMode === APPROVAL_MODE.NONE;
  const disableInputs = approvalMode === APPROVAL_MODE.VERIFY_ALL;

  return {
    ...form,
    currency: marketplace?.marketplaceConfig?.currency,
    onFormSubmit,
    hideInputs,
    disableInputs,
    approvalModeOptions: config.approvalModeOptions,
  };
};
